import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getLeads, patchLead, logError } from '../api';
import LeadFilters from './LeadFilters';
import { 
    isAdmin, 
    isPropietario, 
    isGestion, 
    isEspectador, 
    isComercial, 
    isJefeComerciales,
    getSalesRepId 
} from '../services/AuthService';
import GenericList from './Global/GenericList';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';
import InfoToggle from './Global/InfoToggle';
import LeadCommentsRow from './LeadCommentsRow';
import LeadRegistriesRow from './LeadRegistriesRow';

import LeadKanbanBoard from './LeadKanbanBoard';
import LeadDetailModal from './LeadDetailModal';


const SalesLeadManagement = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [openComments, setOpenComments] = useState({});
    const [openRegistries, setOpenRegistries] = useState({});
    const [loadedRegistries, setLoadedRegistries] = useState({});
    const today = new Date().toISOString().split('T')[0];
    const [searchFilters, setSearchFilters] = useFilterPersistence('filters_sales_lead_management', {
        followUpDateFrom: today,
        followUpDateTo: today
    });
    const [pagination, setPagination] = useFilterPersistence('pagination_sales_lead_management', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;
    const [hasPendingLeads, setHasPendingLeads] = useState(false);
    // Determinar vista por defecto según el rol
    const getDefaultViewMode = () => {
        const isSuperUser = isAdmin() || isPropietario() || isGestion();
        return isSuperUser ? 'table' : 'kanban';
    };
    
    const [viewMode, setViewMode] = useState(getDefaultViewMode()); // 'table' o 'kanban'
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);



    const { orderBy, orderDesc, handleSort } = useSorting();
    const navigate = useNavigate();

    // Determinar permisos del usuario
    const canSelectSalesRep = isAdmin() || isPropietario() || isGestion() || isEspectador() || isJefeComerciales();
    const isOnlyComercial = isComercial() && !canSelectSalesRep;

    // Inicializar filtros para comerciales
    useEffect(() => {
        if (isComercial()) {
            const currentSalesRepId = getSalesRepId();
            if (currentSalesRepId) {
                setSearchFilters(prev => ({
                    ...prev,
                    salesRepId: [currentSalesRepId.toString()]
                }));
            }
        }
    }, []);



    // Cargar leads
    const loadLeads = useCallback(async () => {
        setLoading(true);
        try {
            let filters = { ...searchFilters };
            
            if (isOnlyComercial) {
                // Si es solo comercial, usar su propio ID
                const salesRepId = getSalesRepId();
                if (salesRepId) {
                    filters.salesRepId = salesRepId;
                }
            }
            
            const response = await getLeads(
                currentPage, 
                itemsPerPage, 
                false, 
                orderBy, 
                orderDesc, 
                filters
            );
            
            setLeads(response.items || []);
            setTotalCount(response.totalCount || 0);
            setHasPendingLeads(response.items?.some(lead => lead.hasPendingLeads) || false);
        } catch (error) {
            logError(error);
            console.error('Error loading leads:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, orderBy, orderDesc, searchFilters, isOnlyComercial]);

    useEffect(() => {
        loadLeads();
    }, [loadLeads]);

    const handleSearch = (filters) => {
        setSearchFilters(filters);
        setPagination({ ...pagination, page: 1 });
    };

    const handleResetFilters = () => {
        const baseFilters = {
            followUpDateFrom: new Date().toISOString().split('T')[0],
            followUpDateTo: new Date().toISOString().split('T')[0]
        };
        if (isComercial()) {
            const currentSalesRepId = getSalesRepId();
            if (currentSalesRepId) baseFilters.salesRepId = [currentSalesRepId.toString()];
        }
        setSearchFilters(baseFilters);
        setPagination({ ...pagination, page: 1 });
    };

    const handleManageLead = (lead) => {
        setSelectedLeadId(lead.id);
        setShowDetailModal(true);
    };

    const handleToggleUrgent = async (leadId, currentUrgent) => {
        try {
            const lead = leads.find(l => l.id === leadId);
            await patchLead(leadId, { 
                isUrgent: !currentUrgent,
                statusId: lead?.statusId
            });
            await loadLeads();
        } catch (error) {
            logError(error);
            console.error('Error updating urgent status:', error);
        }
    };

    const columns = [
        { key: 'id', header: 'ID', sortable: true },
        {
            key: 'urgent',
            header: 'Urgente',
            sortable: false,
            render: (lead) => (
                <Button
                    variant={lead.isUrgent ? 'warning' : 'outline-warning'}
                    size="sm"
                    onClick={() => handleToggleUrgent(lead.id, lead.isUrgent)}
                >
                    <i className={`bi ${lead.isUrgent ? 'bi-lightning-fill' : 'bi-lightning'}`}></i>
                </Button>
            )
        },
        { key: 'name', header: 'Nombre', sortable: true },
        { key: 'phone', header: 'Teléfono', sortable: true },
        { key: 'zipCode', header: 'Código Postal', sortable: true },
        {
            key: 'clientOrigin',
            header: 'Origen',
            sortable: true,
            render: (lead) => lead.origin?.name || ''
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (lead) => {
                if (!lead.status?.name) return 'Sin status';
                
                const getStatusColor = (statusId) => {
                    const colors = {
                        1: '#ffc107', // Pendiente de atender - amarillo
                        2: '#17a2b8', // Seguimiento - azul
                        3: '#28a745', // Venta - verde
                        4: '#dc3545'  // No Venta - rojo
                    };
                    return colors[statusId] || '#6c757d';
                };
                
                return (
                    <span 
                        className="badge" 
                        style={{ backgroundColor: getStatusColor(lead.statusId), color: 'white' }}
                    >
                        {lead.status.name}
                    </span>
                );
            }
        },
        {
            key: 'subStatus',
            header: 'Detalle de Estado',
            sortable: true,
            render: (lead) => {
                if (!lead.subStatus?.name) return '';
                
                return (
                    <span className="badge bg-secondary">
                        {lead.subStatus.name}
                    </span>
                );
            }
        },
        ...(canSelectSalesRep ? [{ 
            key: 'salesRepId', 
            header: 'Comercial', 
            sortable: true,
            render: (lead) => lead.salesRep ? `${lead.salesRep.name} ${lead.salesRep.lastName1}` : 'Sin asignar'
        }] : []),
        { 
            key: 'createdTime', 
            header: 'Fecha de Creación', 
            sortable: true,
            render: (lead) => lead.createdTime ? new Date(lead.createdTime).toLocaleString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }) : ''
        },
        { 
            key: 'followUpDate', 
            header: 'Fecha de Seguimiento', 
            sortable: true,
            render: (lead) => lead.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : ''
        },
        {
            key: 'registries',
            header: 'Registros',
            sortable: false,
            render: (lead) => (
                <InfoToggle
                    data-testid={`registries-toggle-${lead.id}`}
                    label="Ver Registros"
                    openState={openRegistries}
                    handleToggle={() => setOpenRegistries(prev => ({ ...prev, [lead.id]: !prev[lead.id] }))}
                    id={lead.id}
                    color="#28a745"
                />
            )
        },
        {
            key: 'comments',
            header: 'Comentarios',
            sortable: false,
            render: (lead) => (
                <InfoToggle
                    data-testid={`comments-toggle-${lead.id}`}
                    label="Ver"
                    openState={openComments}
                    handleToggle={() => setOpenComments(prev => ({ ...prev, [lead.id]: !prev[lead.id] }))}
                    id={lead.id}
                    color="#17a2b8"
                />
            )
        },
        {
            key: 'manage',
            header: 'Gestión',
            sortable: false,
            render: (lead) => (
                <Button
                    data-testid={`manage-lead-${lead.id}`}
                    variant="primary"
                    size="sm"
                    onClick={() => handleManageLead(lead)}
                >
                    Gestionar
                </Button>
            )
        }
    ];

    return (
        <Container fluid className="my-4">
            {hasPendingLeads && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible d-flex align-items-center" role="alert" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                            <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.5em' }}></i>
                            <div>
                                <strong>¡ATENCIÓN!</strong> Tienes leads pendientes de días pasados que requieren seguimiento inmediato. 
                                Gestiona estos leads antes de procesar nuevos.
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
            <LeadFilters
                onSearch={handleSearch}
                onResetFilters={handleResetFilters}
                showAllFilters={true}
                initialFilters={searchFilters}
                collapsible={true}
            />
            <Row className="mb-3">
                <Col xs={12} md={6}>
                    <ButtonGroup>
                        <Button 
                            variant={viewMode === 'kanban' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('kanban')}
                        >
                            <i className="bi bi-kanban me-1"></i>
                            Panel
                        </Button>
                        <Button 
                            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('table')}
                        >
                            <i className="bi bi-table me-1"></i>
                            Tabla
                        </Button>
                    </ButtonGroup>
                </Col>

            </Row>

            {viewMode === 'kanban' ? (
                <LeadKanbanBoard 
                    leads={leads}
                    loading={loading}
                    hasPendingLeads={hasPendingLeads}
                    onReload={loadLeads}
                />
            ) : (
                <GenericList
                    data-testid="leads-table"
                    title={
                        <div className="d-flex justify-content-between align-items-center">
                            <span>Gestión de Leads</span>
                            <span className="badge bg-primary fs-6">{totalCount} registros</span>
                        </div>
                    }
                    data={leads}
                    columns={columns}
                    loading={loading}
                    totalCount={totalCount}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => setPagination({ ...pagination, page })}
                    onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                    emptyMessage="No se encontraron leads asignados."
                    onSort={handleSort}
                    sortable={true}
                    containerFluid={true}
                    renderExpandedRow={(lead) => {
                        const showComments = openComments[lead.id];
                        const showRegistries = openRegistries[lead.id];
                        const totalColumns = columns.length;
                        
                        if (!showComments && !showRegistries) return null;
                        
                        return (
                            <tr>
                                <td colSpan={totalColumns} style={{ padding: 0, border: 'none' }}>
                                    {showRegistries && <LeadRegistriesRow lead={lead} loadedRegistries={loadedRegistries} setLoadedRegistries={setLoadedRegistries} />}
                                    {showComments && <LeadCommentsRow lead={lead} onUpdate={loadLeads} />}
                                </td>
                            </tr>
                        );
                    }}
                />
            )}
            
            <LeadDetailModal
                show={showDetailModal}
                handleClose={() => setShowDetailModal(false)}
                leadId={selectedLeadId}
                onLeadUpdated={loadLeads}
            />

        </Container>
    );
};

export default SalesLeadManagement;